import { isNullish } from "@theorgchartdev/storybook";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { PLACEHOLDER_TABLE_VALUE } from "@/Utils/Constants";
import {
  FormatFieldValue,
  isBlockedField,
  getBlockedFieldDisplayValue,
  sanitizeBlockedValue
} from "@coreapp/utils/FormatFieldValue";
import {
  enumFieldType,
  enumCalcCode,
  enumOverrideType,
  enumObjClassNames,
  kConnectorDict
} from "@coreapp/constants";
import { appstrp } from "@coreapp/utils/appstrp";
import { appstr } from "@coreapp/utils/appstr";
import { gDistinguishNullFieldValues } from "@coreapp/whitelabel/brand";
import { GetDataSourceLogo } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/Utils";

export const IsEditableField = fieldDef => {
  if (!fieldDef) return false;
  const isCalc = fieldDef.GetCalcCode() != null;
  const fieldReadOnly = fieldDef.FieldReadOnly();
  const internalOnly = fieldDef.GetInternalOnly();
  const classDef = fieldDef.GetParentClass();
  const targetFieldID = fieldDef.GetFieldID();
  const recordIDFieldDef = classDef.GetRecordIDFieldDef();
  const hierarchyFieldID = recordIDFieldDef.GetFieldID();
  return !isCalc && !fieldReadOnly && !internalOnly && targetFieldID !== hierarchyFieldID;
};

export const IsBlankValue = value => {
  if (isNullish(value)) return true;
  return value.length === 0;
};

const ShowNotConfigured = fieldDef => {
  if (!gDistinguishNullFieldValues) return false;
  if (fieldDef && fieldDef.GetCalcCode() != null) return false;
  return GetDoc()?.IsOcApiChart() ?? false;
};

// Whether a value should render as "the source never sent this field" (VIZ-20/VIZ-37).
// Single source of truth - SanitizeFieldValue delegates here rather than re-deriving the check.
export const IsFieldNotConfigured = (value, fieldDef) =>
  isNullish(value) && ShowNotConfigured(fieldDef);

export const SanitizeFieldValue = (value, fieldDef, placeholderValue = PLACEHOLDER_TABLE_VALUE) => {
  if (IsBlankValue(value)) {
    if (IsFieldNotConfigured(value, fieldDef)) return appstr("Null - Not configured");
    return placeholderValue;
  }
  if (isBlockedField(value)) return getBlockedFieldDisplayValue();
  return fieldDef ? FormatFieldValue(value, fieldDef) : value;
};

export const sortByKey = (array, key, ascending = true) => {
  return array.sort((a, b) => {
    if (a[key] < b[key]) {
      return ascending ? -1 : 1;
    }
    if (a[key] > b[key]) {
      return ascending ? 1 : -1;
    }
    return 0;
  });
};

export const filterResults = (fVPair, searchTerm) => {
  const displayLabel = fVPair.field;
  const value = fVPair.value;
  const displayValue = sanitizeBlockedValue(value);
  let checkName = (displayLabel || "") + (displayValue || "");
  return checkName.toLowerCase().lastIndexOf(searchTerm.toLowerCase().trim()) !== -1;
};

export const createFieldOverride = (dataObj, delta) => {
  const w = GetWSMgr();
  const d = GetDoc();
  const objDB = d.GetObjDB();

  const objClassType = dataObj.InferObjectClassName();
  const objClass = objDB.GetClassByName(objClassType);

  let changeData = {};
  for (var field in delta) {
    let fieldDef = objClass.GetFieldDefByID(field);
    if (!fieldDef) fieldDef = objClass.GetFieldDefByName(field);
    let curValue = dataObj.GetFieldValue(fieldDef);
    if (curValue !== delta[field]) {
      changeData[field] = delta[field];
    }
  }

  if (Object.keys(changeData).length > 0) {
    w.StartCheckPoint();
    var doc = w.GetDoc();
    var changeObj = {};
    changeObj["data"] = delta;
    doc.SetBoxOverride(dataObj, enumOverrideType.change, changeObj);
    w.EndCheckPoint();
  }
};

export const getShownCountFeedbackText = (origData, filteredData) => {
  return filteredData.length > 0
    ? appstrp("Showing", " ") +
        filteredData.length +
        " " +
        appstrp("of", " ") +
        origData.length +
        " fields."
    : appstrp("No results found", ".");
};

export const getFieldIcon = fieldDef => {
  const calcCode = fieldDef.GetCalcCode();

  const fieldType = fieldDef.GetFieldType();
  if (fieldDef.IsReserved() && calcCode >= -1 && fieldType !== enumFieldType.image) {
    if (calcCode === enumCalcCode.customcalc) {
      return "lambda";
    } else if (calcCode === enumCalcCode.aggregated) {
      return "sigma";
    } else {
      return "function";
    }
  }

  let iconName;

  switch (fieldType) {
    case enumFieldType.text:
      iconName = "font";
      break;
    case enumFieldType.number:
      iconName = "font";
      break;
    case enumFieldType.image:
      iconName = "image";
      break;
    case enumFieldType.date:
      iconName = "calendar";
      break;
    case enumFieldType.object:
      iconName = "function";
      break;
    default:
      iconName = "file-circle-question";
      break;
  }

  return iconName;
};

// const getMofifiedFieldName = (aModObj, srcNum) => {
//   return (
//     aModObj["updateSource"]?.name ??
//     aModObj["type"].charAt(0).toUpperCase() +
//       aModObj["type"].slice(1) +
//       " " +
//       String(srcNum)
//   );
// };

// export const GetModifiedFields = (dbObj, sourceFieldsObj = {}) => {
//   var modFields = [];
//   var fieldDef = dbObj.GetFieldDefByID("_objinfo");
//   if (!fieldDef) return modFields;
//   var objectInfo = dbObj.GetFieldValue(fieldDef);
//   if (!objectInfo) return modFields;
//   objectInfo["modified"] = objectInfo["modified"].sortBy(true, "type");
//   if (objectInfo["modified"] && objectInfo["modified"].length > 0) {
//     var modList = objectInfo["modified"];
//     var lasttype;
//     var modNum = 0;
//     for (var i in modList) {
//       var aModObj = modList[i];
//       var changedFields = aModObj["keys"];
//       if (lasttype != aModObj["type"]) {
//         modNum = parseInt(i);
//       }
//       var srcNum = parseInt(i) + 1 - modNum;
//       lasttype = aModObj["type"];
//       sourceFieldsObj[getMofifiedFieldName(aModObj, srcNum)] = changedFields.map(function (x) {
//         return dbObj.GetFieldDefByName(x);
//       });
//       modFields = modFields.concat(changedFields);
//     }
//   }
//   return modFields;
// };

export const GetMergeAppendFieldSources = (dbObj, fieldSources) => {
  const sourceFieldsObj = {};
  const fieldSourceMap = new Map();
  const mergeAppendFieldNames = new Set();

  var sourceNum = 1;
  fieldSources.append.forEach(source => {
    if (source.keys.size > 0) {
      // this is a set
      const sourceName = "Append" + " " + String(sourceNum);
      sourceFieldsObj[sourceName] = source.keys
        ? Array.from(source.keys)
            .map(header => dbObj.GetFieldDefByName(header))
            .filter(f => f)
        : [];
      fieldSourceMap.set(sourceName, FormatDataSource(source, "append"));
      source.keys.forEach(key => mergeAppendFieldNames.add(key));
      sourceNum++;
    }
  });

  sourceNum = 1;
  fieldSources.merge.forEach(source => {
    if (source.keys.size > 0) {
      // this is a set
      const sourceName = "Merge" + " " + String(sourceNum);
      sourceFieldsObj[sourceName] = source.keys
        ? Array.from(source.keys)
            .map(header => dbObj.GetFieldDefByName(header))
            .filter(f => f)
        : [];
      fieldSourceMap.set(sourceName, FormatDataSource(source, "merge"));
      source.keys.forEach(key => mergeAppendFieldNames.add(key));
      sourceNum++;
    }
  });

  return { sourceFieldsObj, fieldSourceMap, mergeAppendFieldNames };
};

export const FormatDataSource = (source, type) => {
  const connectorList = kConnectorDict[type];
  const connector = connectorList.find(c => c.command === source.source);
  return {
    name: source.override?.name ?? (connector ? connector.connector : source.source),
    icon: GetDataSourceLogo(source.source)
  };
};
